// components/GoogleMapComponent.tsx
import React from "react";
import { useEffect, useState } from "react";
import {
  GoogleMap,
  Marker,
  DirectionsRenderer,
  useLoadScript,
  OverlayView,
} from "@react-google-maps/api";
import parse from "html-react-parser";

import AgencyCard from "./AgencyCard";
import {
  getUniqueAgencies,
  getUniqueData,
  getUniqueLocations,
} from "@/utils/uniqueParse";
import { displacementBetween } from "@/utils/utilFunctions";

const containerStyle = {
  width: "100%",
  height: "400px",
  borderRadius: "10px",
};

interface Location {
  lat: number;
  lng: number;
}

interface GoogleMapComponentProps {
  name: string;
  cardata?: CarData[];
  airports: AirportCoordinate;
  location: Location;
  airport: string;
  airportData: AirportNameData;
  locale: string;
}

const CustomMarker = ({ url }: { url: string }) => {
  return (
    <div className="px-4 py-2 bg-white rounded-tl-none shadow-2xl rounded-xl border-[#E5EAF6] border">
      <img
        loading="lazy"
        src={url}
        className="lg:h-[24px] h-[20px]"
        alt={`agency map logo`}
      />
    </div>
  );
};

const GoogleMapComponent: React.FC<GoogleMapComponentProps> = ({
  name,
  cardata,
  airports,
  location,
  airport,
  airportData,
  locale,
}) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY!,
    libraries: ["places"],
  });

  const airportLocation = airport
    .split("_")
    .join(", ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
  const [map, setMap] = useState<any>(null);
  const [sliceLength, setSliceLength] = useState<number>(4);

  const uniqueData: { provider: Provider; agency: Agency }[] =
    getUniqueAgencies(cardata || []);

  const countMap: any = {};
  uniqueData.forEach((item) => {
    const providerCode = item.agency.providerCode;
    countMap[providerCode] = (countMap[providerCode] || 0) + 1;
  });

  uniqueData.sort(
    (a, b) => countMap[a.agency.providerCode] - countMap[b.agency.providerCode]
  );

  const [selectedLocation, setSelectedLocation] = useState<{
    provider: Provider;
    agency: Agency;
  } | null>(null);
  const [directions, setDirections] =
    useState<google.maps.DirectionsResult | null>(null);

  if (loadError) {
    return <div>Error loading Google Maps</div>;
  }

  const providerAvg = () => {
    let lat = 0;
    let lon = 0;
    uniqueData.forEach((item) => {
      lat += item.agency.pickupLocation.lat;
      lon += item.agency.pickupLocation.lon;
    });
    return {
      lat: lat / uniqueData.length,
      lon: lon / uniqueData.length,
    };
  };

  const airportCoords = (airportList: AirportCoordinate | undefined) => {
    for (const i in airportList) {
      if (i === (name.match(/\[([^\]]+)\]/) || [])[1]) {
        return {
          lat: airportList[i].airportLatitude,
          lon: airportList[i].airportLongitude,
          code: i,
        };
      }
    }
    return {
      lat: location.lat,
      lon: location.lng,
      code: "N/A",
    };
  };
  const airportCoord = airportCoords(airports) || {
    lat: airports.lat,
    lon: airports.lon,
    code: "N/A",
  };
  const center: { lat: number; lng: number } = {
    lat: (airportCoord.lat + providerAvg().lat) / 2,
    lng: (airportCoord.lon + providerAvg().lon) / 2,
  } || {
    lat: 0,
    lng: 0,
  };

  function findClosestAgency(
    data: any[],
    targetLat: number,
    targetLon: number
  ): {
    agency: Agency;
    provider: Provider;
  } | null {
    let closestElement: {
      agency: Agency;
      provider: Provider;
    } | null = null;
    let closestDistance = Number.MAX_VALUE;

    for (const element of data) {
      const agencyLat = element.agency.pickupLocation.lat;
      const agencyLon = element.agency.pickupLocation.lon;

      // Calculate the distance using Haversine formula
      const distance = displacementBetween(
        {
          lat: targetLat,
          lon: targetLon,
        },
        {
          lat: agencyLat,
          lon: agencyLon,
        }
      );

      if (distance < closestDistance) {
        closestDistance = distance;
        closestElement = element;
      }
    }

    return closestElement;
  }

  const agencyCounter = (data: any[]) => {
    let atAirport: number = 0;
    let atTerminal: number = 0;
    let away: number = 0;
    const total = data.length;
    data.forEach((item) => {
      if (item.agency.pickupLocation?.locType === "IN_TERMINAL") atTerminal++;
      else if (item.agency.pickupLocation?.locCategory === "AT_AIRPORT")
        atAirport++;
      else away++;
    });
    return {
      atAirport,
      atTerminal,
      away,
      total,
    };
  };

  const generateMapText = (
    name: string,
    airportLocation: string,
    data?: CarData[]
  ) => {
    if (!data)
      return `<p>If you are in ${airportLocation}, you can find the best car rental deal using the "Car rental near me from ${name}" option on our website. This map shows you the closest rental car pick-up locations at a walkable distance from you in ${name}.</p><p>You can also find nearby pick-up locations on the map to see if a better deal is available. For example, at ${name}, renting a car from a locations outside ${name} can save you money. So, you might want to check them out.</p></p><p>`;

    const uniqueLocations = getUniqueLocations(data);
    const uniqueAgencies = getUniqueAgencies(data);
    const nearestAgency = findClosestAgency(
      uniqueLocations,
      airportCoord.lat,
      airportCoord.lon
    );

    const { atTerminal, atAirport, away, total } =
      agencyCounter(uniqueLocations);
    const defaultText = `
    <p>If you are in ${airportData.airportName}, you can find the best car rental deal using the "Car rental near me from ${name}" option on our website. This map shows you the closest rental car pick-up location in ${name}. As you can see <strong className="font-bold">${nearestAgency?.agency.name}</strong> is the nearest car rental company in ${name}.</p>
    <p>Our users prefer ${nearestAgency?.agency.name} as their favorite rental car partner in ${name}.</p>
    <p>You can also check out nearby pick-up locations on the map to see if a better deal is available. For example, airport fees and taxes could make renting a car from ${name} expensive. You can avoid paying these by choosing a location outside ${name}. So, be sure to check them.</p>
    `;

    const onlyTerminalMultipleAgency = `
    <p>If you are in ${
      airportData.airportName
    }, you can find the best car rental deal using the "Car rental near me from ${name}" option on our website. This map shows you the closest rental car pick-up locations at a walkable distance from you in ${name}. As you can see, <strong className="font-bold">${
      uniqueAgencies.length === 1
        ? `${uniqueAgencies[0].agency.name}`
        : `${uniqueAgencies
            .map((item) => item.agency.name)
            .join(", ")
            .replace(/, ([^,]*)$/, " and $1")}`
    }</strong> offer${
      uniqueAgencies.length === 1 ? `s` : ``
    } terminal pick up at ${name}.
    <p>Our users prefer ${
      nearestAgency?.agency.name
    } as their favorite rental car partner in ${name}.
    But be sure to check out others, as they might have impressive offers waiting for you.
    </p>    
    `;

    const onlyTerminalSingleAgency = `
    <p>If you are in ${airportData.airportName}, you can find the best car rental deal using the "Car rental near me from ${name}" option on our website. This map shows you the closest rental car pick-up locations at a walkable distance from you in ${name}. As you can see <strong className="font-bold">${nearestAgency?.agency.name}</strong> offers terminal pick up at ${name}. </p>
    <p>With great deals to offer, ${nearestAgency?.agency.name} is guaranteed to give you a fantastic renting experience.</p>
    `;

    const airportAndTerminalMultipleAgency = `
    <p>If you are in ${
      airportData.airportName
    }, you can find the best car rental deal using the "Car rental near me from ${name}" option on our website. This map shows you the closest rental car pick-up locations at a walkable distance from you in ${name}. As you can see, <strong className="font-bold">${
      uniqueAgencies.length === 1
        ? `${uniqueAgencies[0].agency.name}`
        : `${uniqueAgencies
            .map((item) => item.agency.name)
            .join(", ")
            .replace(/, ([^,]*)$/, " and $1")}`
    }</strong> offer${
      uniqueAgencies.length === 1 ? `s` : ``
    } terminal pick-up and have pick-up locations inside ${name}.</p>
    <p>Our users prefer ${
      nearestAgency?.agency.name
    } as their favorite rental car partner in ${name}.But be sure to check out others, as they might have impressive offers waiting for you.</p>
    `;

    const airportAndTerminalSingleAgency = `
    <p>If you are in ${airportData.airportName}, you can find the best car rental deal using the "Car rental near me from ${name}" option on our website. This map shows you the closest rental car pick-up locations at a walkable distance from you in ${name}. As you can see, <strong className="font-bold">${nearestAgency?.agency.name}</strong> offers terminal pick-up and has a pick-up location inside ${name}. </p>
    <p>With great deals to offer, ${nearestAgency?.agency.name} is guaranteed to give you a fantastic renting experience.</p>
    `;

    const airportOnlyMultipleAgency = `<p>If you are in ${
      airportData.airportName
    }, you can find the best car rental deal using the "Car rental near me from ${name}" option on our website. This map shows you the closest rental car pick-up locations at a walkable distance from you in ${name}. As you can see, <strong className="font-bold">${
      uniqueAgencies.length === 1
        ? `${uniqueAgencies[0].agency.name}`
        : `${uniqueAgencies
            .map((item) => item.agency.name)
            .join(", ")
            .replace(/, ([^,]*)$/, " and $1")}`
    }</strong>  have pick-up locations inside ${name}. </p>`;

    const airportOnlySingleAgency = `
    <p>If you are in ${airportData.airportName}, you can find the best car rental deal using the "Car rental near me from ${name}" option on our website. This map shows you the closest rental car pick-up locations at a walkable distance from you in ${name}. As you can see, <strong className="font-bold">${nearestAgency?.agency.name}</strong> has a pick-up location inside ${name}. </p>
    <p>With great deals to offer, ${nearestAgency?.agency.name} is guaranteed to give you a fantastic renting experience.</p>
    `;
    if (away > 0) return defaultText;
    else if (atTerminal === total && total > 1)
      return onlyTerminalMultipleAgency;
    else if (atTerminal === total && total === 1)
      return onlyTerminalSingleAgency;
    else if (atTerminal > 0 && atAirport > 0 && total > 1)
      return airportAndTerminalMultipleAgency;
    else if (atTerminal > 0 || (atAirport > 0 && total === 1))
      return airportAndTerminalSingleAgency;
    else if (atAirport === total && total > 1) return airportOnlyMultipleAgency;
    else if (atAirport === total && total === 1) return airportOnlySingleAgency;
    else return defaultText;
  };

  return (
    <div className="mt-5 lg:mt-[60px] bg-white px-[13px] py-[30px]">
      <div className="mx-auto max-w-7xl">
        <div className="flex justify-between w-full">
          <div className="space-y-[6px] lg:space-y-[10px] mb-[18px]">
            <h2 className="text-base font-extrabold lg:text-3xl text-my-dark-blue">
              Car Rental Directions from {name}
            </h2>
            <div className="space-y-6 text-sm font-normal text-my-light-blue lg:font-light lg:text-base">
              {parse(generateMapText(name, airportLocation, cardata))}
            </div>
          </div>
        </div>

        <div>
          {cardata && isLoaded ? (
            <GoogleMap
              options={{
                disableDefaultUI: true,
                zoomControl: true,
                styles: mapStyles,
              }}
              mapContainerStyle={containerStyle}
              center={center}
              zoom={20}
              onLoad={(map) => {
                setMap(map);
                const bounds = new window.google.maps.LatLngBounds();
                uniqueData.forEach((item) => {
                  bounds.extend(
                    new google.maps.LatLng(
                      item.agency.pickupLocation.lat,
                      item.agency.pickupLocation.lon
                    )
                  );
                });
                bounds.extend(new google.maps.LatLng(center.lat, center.lng));
                bounds.extend(
                  new google.maps.LatLng(
                    airportCoords(airports).lat,
                    airportCoords(airports).lon
                  )
                );
                map.fitBounds(bounds);
              }}
              onUnmount={() => setMap(null)}
            >
              {/* <Marker position={userLocation || center} /> */}

              {/* {center && cardata
                ? uniqueData.map(
                    (
                      item: { agency: Agency; provider: Provider },
                      index: number
                    ) =>
                      item.agency.providerCode ===
                      selectedLocation?.agency.providerCode ? null : (
                        <Marker
                          key={index}
                          position={{
                            lat: item.agency.pickupLocation.lat,
                            lng: item.agency.pickupLocation.lon,
                          }}
                          title={item.agency.name}
                          icon={{
                            url: "/map-marker.svg",
                          }}
                        />
                      )
                  )
                : null} */}
              {cardata ? (
                uniqueData
                  .filter(
                    (a) =>
                      !a.agency.name.includes("Surprise Agency") &&
                      !a.agency.providerCode.includes("SPECIALRENTALCAR")
                  )
                  .slice(0, 8)
                  .map(
                    (
                      item: { agency: Agency; provider: Provider },
                      index: number
                    ) => (
                      <OverlayView
                        key={index}
                        position={{
                          lat: item.agency.pickupLocation.lat,
                          lng: item.agency.pickupLocation.lon,
                        }}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                      >
                        <CustomMarker
                          url={
                            item.agency.name === "Surprise Agency"
                              ? "/rental-company-list/surprise.svg"
                              : `https://content.r9cdn.net/rimg/provider-logos/cars/h/${item.agency.icon}`
                          }
                        />
                        {/* 
                        FIXME: Used a static CDN URL instead of going through the process of fetching the complete URL from a request and going back and forth like crazy. This is like a quick hack...
                        FIXME: If the images break, try looking at the provider icons which do have the full URL, to see if the CDN URL is still the same.
                      */}
                      </OverlayView>
                    )
                  )
              ) : (
                <></>
              )}
              {/* {selectedLocation && (
                <OverlayView
                  position={{
                    lat: selectedLocation.agency.pickupLocation.lat,
                    lng: selectedLocation.agency.pickupLocation.lon,
                  }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <CustomMarker url={selectedLocation.provider.logoUrl} />
                </OverlayView>
              )} */}
              {airports && (
                <Marker
                  position={{
                    lat: airportCoord.lat,
                    lng: airportCoord.lon,
                  }}
                  title={airportCoord.code}
                  icon={{
                    url: "/airport-pin.svg",
                  }}
                />
              )}
              {directions && <DirectionsRenderer directions={directions} />}
            </GoogleMap>
          ) : (
            <></>
          )}
        </div>
        <div className="grid grid-cols-1 py-4 lg:grid-cols-2 lg:gap-2">
          {cardata &&
            (uniqueData || getUniqueData(cardata))
              .filter(
                (a) =>
                  !a.agency.name.includes("Surprise Agency") &&
                  !a.agency.providerCode.includes("SPECIALRENTALCAR")
              )
              .slice(0, sliceLength)
              .map(
                (
                  item: { provider: Provider; agency: Agency },
                  index: number
                ) => (
                  <AgencyCard
                    key={index}
                    agency={item.agency}
                    provider={item.provider}
                    airportData={airportData}
                    airportCoords={{
                      lat: airportCoords(airports).lat,
                      lon: airportCoords(airports).lon,
                    }}
                    locale={locale}
                  />
                )
              )}
        </div>
        {cardata && (uniqueData || getUniqueData(cardata)).length > 4 && (
          <div className="flex justify-end w-full">
            <p
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                if (sliceLength === 4) setSliceLength(uniqueData.length);
                else setSliceLength(4);
              }}
              className="cursor-pointer select-none w-fit text-[#004DE3] hover:underline"
            >
              {`Show ${
                sliceLength === 4 && (cardata ?? [])?.length > 4
                  ? "More"
                  : "Less"
              } Providers `}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(GoogleMapComponent);

const mapStyles = [
  {
    featureType: "administrative.land_parcel",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "administrative.neighborhood",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "landscape.man_made",
    stylers: [
      {
        visibility: "simplified",
      },
    ],
  },
  {
    featureType: "poi",
    elementType: "labels.text",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "poi.business",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "road",
    elementType: "labels",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "road",
    elementType: "labels.icon",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "road.arterial",
    elementType: "labels",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "road.highway",
    elementType: "labels",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "road.local",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "transit",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "transit.station.airport",
    stylers: [
      {
        visibility: "on",
      },
    ],
  },
  {
    featureType: "transit.station.airport",
    elementType: "labels.icon",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "water",
    elementType: "labels.text",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
];
